import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { StepLayout } from "../components/StepLayout";
import {
  getMerchantStepPath,
  PROVIDER_TYPE,
  getResumeStep,
} from "../constants";
import { useAuth } from "@/providers/auth";
import { useCreateApplication } from "@/queries/provider-onboarding/use-create-application";
import { useApplicationInfo } from "@/queries/provider-onboarding/use-application-info";
import { getApplicationInfo } from "@/api/v1-jwt/provider-onboarding/provider-onboarding";
import providerOnboardingQueryKeys from "@/queries/provider-onboarding/query-keys";
import { toast } from "react-toastify";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown, Check } from "lucide-react";
import { cn } from "@/utils/classnames";

const SUPPORT_EMAIL = "support@pocketbook.tech";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const formatCurrency = (value: string): string => {
  const digits = value.replace(/\D/g, "");
  if (!digits) return "";
  return Number(digits).toLocaleString("en-US");
};

const CurrencyField = ({
  label,
  value,
  onChange,
  placeholder,
  error,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  placeholder?: string;
  error?: string;
}) => (
  <div className="space-y-1.5">
    <Label>{label}</Label>
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm select-none">
        $
      </span>
      <Input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(formatCurrency(e.target.value))}
        placeholder={placeholder}
        className={cn(
          "pl-6",
          error && "border-red-400 focus-visible:ring-red-400",
        )}
      />
    </div>
    {error && <p className="text-xs text-red-500">{error}</p>}
  </div>
);

const MonthsMultiSelect = ({
  selected,
  onChange,
  error,
}: {
  selected: string[];
  onChange: (months: string[]) => void;
  error?: string;
}) => {
  const toggle = (month: string) => {
    onChange(
      selected.includes(month)
        ? selected.filter((m) => m !== month)
        : [...selected, month],
    );
  };

  const label =
    selected.length === 0
      ? "Select months..."
      : selected.length === 12
      ? "All months"
      : selected.join(", ");

  return (
    <div className="space-y-1.5">
      <Label>What months of the year does your business operate?</Label>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              "flex h-10 w-full items-center justify-between rounded-lg border border-input bg-background px-3 py-2 text-sm text-left",
              error && "border-red-400",
            )}
          >
            <span
              className={cn(selected.length === 0 && "text-muted-foreground")}
            >
              {label}
            </span>
            <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2" align="start">
          <div className="grid grid-cols-3 gap-1">
            {MONTHS.map((month) => {
              const isSelected = selected.includes(month);
              return (
                <button
                  key={month}
                  type="button"
                  onClick={() => toggle(month)}
                  className={cn(
                    "flex items-center gap-1.5 rounded px-2 py-1.5 text-sm transition-colors",
                    isSelected
                      ? "bg-gray-900 text-white"
                      : "hover:bg-gray-50 text-gray-700",
                  )}
                >
                  <Check
                    className={cn(
                      "h-3.5 w-3.5 shrink-0",
                      isSelected ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {month.slice(0, 3)}
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
};

interface FormState {
  highestTransaction: string;
  avgMonthlyVolume: string;
  isSeasonal: string;
  operatingMonths: string[];
  personalGuaranty: string;
  customerSurcharge: string;
}

interface FormErrors {
  highestTransaction?: string;
  avgMonthlyVolume?: string;
  isSeasonal?: string;
  operatingMonths?: string;
  personalGuaranty?: string;
  customerSurcharge?: string;
}

const parseCurrency = (value: string): number =>
  Number(value.replace(/,/g, "")) || 0;

const Step1 = () => {
  const [form, setForm] = useState<FormState>({
    highestTransaction: "",
    avgMonthlyVolume: "",
    isSeasonal: "",
    operatingMonths: [],
    personalGuaranty: "",
    customerSurcharge: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const history = useHistory();
  const queryClient = useQueryClient();
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const { data: applicationInfo } = useApplicationInfo(userId!, PROVIDER_TYPE);

  useEffect(() => {
    if (applicationInfo?.external_application_id && applicationInfo?.status) {
      history.replace(
        getResumeStep(
          applicationInfo.status,
          applicationInfo.external_application_id,
        ),
      );
    }
  }, [applicationInfo, history]);

  const checkApplicationAfterTimeout = async (): Promise<boolean> => {
    try {
      const info = await getApplicationInfo(userId!, PROVIDER_TYPE);
      if (info?.external_application_id && info?.status) {
        queryClient.invalidateQueries({
          queryKey: providerOnboardingQueryKeys.all,
        });
        history.push(getResumeStep(info.status, info.external_application_id));
        return true;
      }
    } catch {
      // ignore — show the generic error below
    }
    return false;
  };

  const { mutate: createApplication, isPending: isSubmitting } =
    useCreateApplication({
      onSuccess: (data) => {
        history.push(getMerchantStepPath(2, data.application_id));
      },
      onError: async (error: any) => {
        const is504 =
          error?.response?.status === 504 ||
          error?.response?.data?.message === "Endpoint request timed out";
        if (is504) {
          const navigated = await checkApplicationAfterTimeout();
          if (!navigated) {
            toast.error(
              "The server is taking longer than expected. Please try again in a moment.",
              { autoClose: 8000 },
            );
          }
        } else {
          toast.error("Failed to submit application. Please try again.");
        }
      },
    });

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!form.highestTransaction)
      e.highestTransaction = "This field is required.";
    if (!form.avgMonthlyVolume) e.avgMonthlyVolume = "This field is required.";
    if (!form.isSeasonal) e.isSeasonal = "Please select an option.";
    if (form.isSeasonal === "yes" && form.operatingMonths.length === 0)
      e.operatingMonths = "Please select at least one month.";
    if (!form.personalGuaranty) e.personalGuaranty = "Please select an option.";
    if (!form.customerSurcharge)
      e.customerSurcharge = "Please select an option.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (!validate() || !userId) return;
    createApplication({
      userId,
      providerType: PROVIDER_TYPE,
      payload: {
        is_personal_guarantor: form.personalGuaranty === "yes",
        estimated_highest_transaction_amount: parseCurrency(
          form.highestTransaction,
        ),
        estimated_average_monthly_volume: parseCurrency(form.avgMonthlyVolume),
        is_business_seasonal: form.isSeasonal === "yes",
        business_seasonal_months: form.operatingMonths,
        add_customer_surcharge: form.customerSurcharge === "yes",
        default_credit_card_payment_invoice: true,
      },
    });
  };

  return (
    <StepLayout
      step={1}
      title="Additional Business Information"
      onNext={handleNext}
      nextDisabled={isSubmitting}
      nextLabel="Next"
      isLoading={isSubmitting}
    >
      <p className="text-sm text-gray-600 leading-relaxed -mt-2">
        If any of your business information or personal information has changed,
        please{" "}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="underline text-gray-800 hover:text-gray-900"
        >
          contact us
        </a>{" "}
        before starting this application. Your business and personal information
        will be shared with our partner, Deluxe, to process your application.
      </p>

      <CurrencyField
        label="What is your estimated single highest credit card transaction per month?"
        value={form.highestTransaction}
        onChange={(val) => set("highestTransaction", val)}
        error={errors.highestTransaction}
      />

      <CurrencyField
        label="What is your average total sales volume per month?"
        value={form.avgMonthlyVolume}
        onChange={(val) => set("avgMonthlyVolume", val)}
        error={errors.avgMonthlyVolume}
      />

      <div className="space-y-1.5">
        <Label>Is your business seasonal?</Label>
        <Select
          value={form.isSeasonal}
          onValueChange={(val) => {
            set("isSeasonal", val);
            if (val === "no") set("operatingMonths", []);
          }}
        >
          <SelectTrigger className={cn(errors.isSeasonal && "border-red-400")}>
            <SelectValue placeholder="Yes/No" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
        {errors.isSeasonal && (
          <p className="text-xs text-red-500">{errors.isSeasonal}</p>
        )}
      </div>

      {form.isSeasonal === "yes" && (
        <MonthsMultiSelect
          selected={form.operatingMonths}
          onChange={(months) => set("operatingMonths", months)}
          error={errors.operatingMonths}
        />
      )}

      <div className="pt-2">
        <h3 className="text-sm font-bold text-gray-900 mb-1">
          Individual Guaranty
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-3">
          Will you agree to take responsibility for the business's obligations
          to Deluxe if the business is unable to? This is not required. If you
          opt-out, additional information such as business financials or
          business bank statements may be requested.
        </p>
        <Label className="mb-1.5 block">
          I agree to the individual guaranty.
        </Label>
        <Select
          value={form.personalGuaranty}
          onValueChange={(val) => set("personalGuaranty", val)}
        >
          <SelectTrigger
            className={cn(errors.personalGuaranty && "border-red-400")}
          >
            <SelectValue placeholder="Yes/No" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
        {errors.personalGuaranty && (
          <p className="text-xs text-red-500 mt-1">{errors.personalGuaranty}</p>
        )}
        <p className="text-xs text-gray-500 mt-2">
          You will have an opportunity to review the full terms of the
          Individual Guaranty before submitting your application.
        </p>
      </div>

      <div className="pt-2">
        <h3 className="text-sm font-bold text-gray-900 mb-1">
          Customer Surcharge
        </h3>
        <p className="text-sm text-gray-600 leading-relaxed mb-3">
          Surcharge your customers to help offset your processing costs.
          Surcharges apply to card transactions only and must comply with state
          and card-brand rules.
        </p>
        <Select
          value={form.customerSurcharge}
          onValueChange={(val) => set("customerSurcharge", val)}
        >
          <SelectTrigger
            className={cn(errors.customerSurcharge && "border-red-400")}
          >
            <SelectValue placeholder="Yes/No" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="yes">Yes</SelectItem>
            <SelectItem value="no">No</SelectItem>
          </SelectContent>
        </Select>
        {errors.customerSurcharge && (
          <p className="text-xs text-red-500 mt-1">
            {errors.customerSurcharge}
          </p>
        )}
      </div>
    </StepLayout>
  );
};

export default Step1;
